/**
 * Small-group suppression (n < 10) — the one place the client applies the rule.
 *
 * The datahouse flags any score cell whose distinct learners are under the floor
 * (`suppressed: true`) and omits its value / band / avg; a dashboard whose own
 * population is under the floor carries `scope_suppressed` + `scope_n` on its
 * registry document. The client must never draw a flagged value, even from an
 * older cached payload that still carries one (the API's Redis layer keeps a
 * dashboard for up to two hours), so every payload is normalised on arrival:
 * flagged cells get their scores nulled here, and the views render the
 * suppressed state from the flag alone.
 *
 * Participation and program-operations fields (rate, participants, total,
 * enrolled, completed …) are never touched: they are operational information,
 * not assessment results (decision 7 Oct 2026).
 *
 * Pure module — no React, no MUI — so the service layer, the card readers and
 * the tests can all import it.
 */
export const SUPPRESSION_MIN_N = 10;
export const SUPPRESSED_COPY = `suppressed — fewer than ${SUPPRESSION_MIN_N} students`;

const isObj = (o) => !!o && typeof o === 'object' && !Array.isArray(o);

/** A table cell's cell_data, a bar item's data, a KPI's kpi_data … */
export const isSuppressedCell = (cd) => isObj(cd) && cd.suppressed === true;
/** A bar / chart item: the flag at the top level or inside its raw cell. */
export const isSuppressedItem = (item) => isObj(item) && (item.suppressed === true || isSuppressedCell(item.data));
/** A KPICard: top-level flag (new contract) or inside kpi_data (older payloads). */
export const isSuppressedKpi = (card) => isObj(card) && (card.suppressed === true || isSuppressedCell(card.kpi_data));
/** A benchmark tick (benchmark_values[]) or a KPI add-info. */
export const isSuppressedBenchmark = (b) => isObj(b) && (b.suppressed === true || isSuppressedCell(b.data));
/** One side of a MovementCard (baseline / reassess). */
export const isSuppressedCycle = (c) => isObj(c) && c.suppressed === true;
/** The whole dashboard: its own population is under the floor. */
export const isScopeSuppressed = (dashboardData) => dashboardData?.registry?.scope_suppressed === true;
export const scopeN = (dashboardData) => {
    const n = dashboardData?.registry?.scope_n;
    return n === undefined || n === null ? null : n;
};

/** A raw cell with its scores removed; n / n_users and participation fields stay. */
const maskCell = (cd) => (isObj(cd) ? {...cd, value: null, avg: null, band: null, suppressed: true} : cd);

const maskBenchmark = (b) => (isSuppressedBenchmark(b) ? {...b, suppressed: true, value: null, band: null, data: maskCell(b.data)} : b);

const normaliseCard = (card) => {
    if (!isObj(card)) return card;
    let out = card;

    if (card.card_type === 'KPICard') {
        const infos = Array.isArray(card.kpi_add_infos)
            ? card.kpi_add_infos.map((i) => (isSuppressedBenchmark(i) ? {...i, suppressed: true, info_value: null, band: null} : i))
            : card.kpi_add_infos;
        out = isSuppressedKpi(card)
            ? {...card, suppressed: true, kpi_value: null, kpi_band: null, kpi_data: maskCell(card.kpi_data), kpi_add_infos: infos}
            : {...card, kpi_add_infos: infos};
    }

    if (Array.isArray(card.items)) {                       // BarChartCard / BarChartBMCard
        out = {
            ...out,
            items: card.items.map((it) => {
                if (!isObj(it)) return it;
                const bvs = Array.isArray(it.benchmark_values) ? it.benchmark_values.map(maskBenchmark) : it.benchmark_values;
                return isSuppressedItem(it)
                    ? {...it, suppressed: true, value: null, band: null, data: maskCell(it.data), benchmark_values: bvs}
                    : {...it, benchmark_values: bvs};
            }),
        };
    }

    if (Array.isArray(card.table_objects)) {               // TableCard / TableRubricCard / PriorityTableCard / ORGCard
        out = {
            ...out,
            table_objects: card.table_objects.map((col) => {
                if (!isObj(col) || !Array.isArray(col.cell_data) || !Array.isArray(col.values)) return col;
                const idx = col.cell_data.map((cd, i) => (isSuppressedCell(cd) ? i : -1)).filter((i) => i >= 0);
                if (!idx.length) return col;
                const values = col.values.slice();
                const cell_data = col.cell_data.slice();
                idx.forEach((i) => { values[i] = null; cell_data[i] = maskCell(cell_data[i]); });
                return {...col, values, cell_data};
            }),
        };
    }

    if (card.card_type === 'MovementCard') {
        const cyc = (c) => (isSuppressedCycle(c) ? {...c, value: null, band: null} : c);
        const any = isSuppressedCycle(card.baseline) || isSuppressedCycle(card.reassess);
        const bench = (b) => {
            if (!isObj(b)) return b;
            const sb = b.suppressed === true || b.suppressed_baseline === true;
            const sr = b.suppressed === true || b.suppressed_reassess === true;
            return sb || sr ? {...b, baseline: sb ? null : b.baseline, reassess: sr ? null : b.reassess} : b;
        };
        out = {
            ...out,
            baseline: cyc(card.baseline),
            reassess: cyc(card.reassess),
            delta: any ? null : card.delta,
            band_change: any ? null : card.band_change,
            benchmarks: Array.isArray(card.benchmarks) ? card.benchmarks.map(bench) : card.benchmarks,
            matched: isSuppressedCell(card.matched) ? {...card.matched, baseline: null, reassess: null, delta: null} : card.matched,
        };
    }

    if (card.card_type === 'ProgramImpactCard' && card.suppressed === true) {
        out = {...out, pre_avg: null, post_avg: null, impact_rate: null, pass_rate: null, signal: null};
    }

    return out;
};

/**
 * The dashboard payload ({registry, cards}) with every flagged score nulled and the
 * scope flag in its canonical place (registry). Idempotent; unflagged cells are
 * returned as they came.
 */
export const normaliseDashboardPayload = (data) => {
    if (!isObj(data)) return data;
    const registry = isObj(data.registry) ? data.registry : {};
    const cards = isObj(data.cards) ? data.cards : null;
    const scope_suppressed = registry.scope_suppressed === true || cards?.scope_suppressed === true;
    const n = registry.scope_n ?? cards?.scope_n;
    const scope_n = n === undefined || n === null ? null : n;
    const sections = Array.isArray(cards?.sections)
        ? cards.sections.map((s) => (isObj(s) ? {...s, cards: Array.isArray(s.cards) ? s.cards.map(normaliseCard) : s.cards} : s))
        : cards?.sections;
    return {
        ...data,
        registry: {...registry, scope_suppressed, scope_n},
        cards: cards ? {...cards, sections} : data.cards,
    };
};

/** The service response ({data, …}) with its payload normalised. */
export const normaliseDashboardResponse = (res) => (
    isObj(res) && isObj(res.data) ? {...res, data: normaliseDashboardPayload(res.data)} : res
);
